/**
 * API Service
 * Centralized API client for handling all API calls with consistent error handling and authentication
 */

// API base URL - can be configured based on environment
const API_BASE_URL = "https://api.hmdl.xyz";
// const API_BASE_URL = "http://localhost:5001";

// Error codes from backend
const ERROR_CODES = {
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_MISSING: 'TOKEN_MISSING',
  USER_NOT_FOUND: 'USER_NOT_FOUND'
};

// Global error handler
let onAuthError = null;

/**
 * Set global auth error handler
 * @param {Function} handler - Function to handle auth errors
 */
export const setAuthErrorHandler = (handler) => {
  onAuthError = handler;
};

/**
 * Make an API request with standard options
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} options.body - Request body (will be JSON stringified)
 * @param {string} options.token - Authentication token
 * @returns {Promise<any>} - Response data
 */
async function apiRequest(endpoint, options = {}) {
  const {
    method = "GET",
    body,
    token,
    headers: customHeaders = {},
  } = options;

  // Ensure endpoint starts with '/' and ends with '/' to prevent 308 redirects
  const normalizedEndpoint = endpoint.startsWith("/") 
    ? endpoint 
    : `/${endpoint}`;
  
  const finalEndpoint = normalizedEndpoint.endsWith("/") || normalizedEndpoint.includes("?")
    ? normalizedEndpoint
    : `${normalizedEndpoint}/`;

  // Build request headers
  const headers = {
    "Content-Type": "application/json",
    "Accept": "application/json",
    ...customHeaders,
  };

  // Add authorization header if token is provided
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Build request options
  const requestOptions = {
    method,
    headers,
    mode: "cors",
    credentials: "omit",
  };

  // Add body if provided (for non-GET requests)
  if (body && method !== "GET") {
    requestOptions.body = JSON.stringify(body);
    console.log(`Request body for ${method} ${finalEndpoint}:`, body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${finalEndpoint}`, requestOptions);
    const data = await response.json();

    // Handle auth errors
    if (!data.success && data.error_code && onAuthError) {
      if ([ERROR_CODES.TOKEN_EXPIRED, ERROR_CODES.TOKEN_INVALID, ERROR_CODES.TOKEN_MISSING, ERROR_CODES.USER_NOT_FOUND].includes(data.error_code)) {
        onAuthError(data.error_code);
      }
    }

    if (!response.ok) {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * Authentication API
 */
export const authApi = {
  /**
   * Google OAuth login/signup
   * @param {Object} authData - Object containing Google access token
   * @param {string} authData.access_token - Google OAuth access token
   * @returns {Promise<Object>} - User data and token
   */
  googleAuth: (authData) => {
    console.log('Google auth data being sent to backend:', authData);
    return apiRequest("/api/auth/oauth/google", {
      method: "POST",
      body: authData,
    });
  },
  
  /**
   * GitHub OAuth login/signup
   * @param {Object} authData - Object containing GitHub access code
   * @param {string} authData.access_code - GitHub OAuth access code
   * @returns {Promise<Object>} - User data and token
   */
  githubAuth: (authData) => {
    console.log('GitHub auth data being sent to backend:', authData);
    return apiRequest("/api/auth/oauth/github", {
      method: "POST",
      body: authData,
    });
  },
  
  /**
   * Send email verification code
   * @param {string} email - User email
   * @returns {Promise<Object>} - Response data
   */
  sendVerificationCode: (email) => {
    return apiRequest("/api/auth/email/send-code", {
      method: "POST",
      body: { email },
    });
  },
  
  /**
   * Verify email with code
   * @param {string} email - User email
   * @param {string} code - Verification code
   * @returns {Promise<Object>} - User data and token
   */
  verifyEmail: (email, code) => {
    return apiRequest("/api/auth/email/verify", {
      method: "POST",
      body: { email, code },
    });
  },

  /**
   * Bind GitHub account to current user
   * @param {Object} authData - Object containing GitHub access code
   * @param {string} authData.access_code - GitHub OAuth access code
   * @param {string} token - User's authentication token
   * @returns {Promise<Object>} - Response data
   */
  bindGithubAccount: (authData, token) => {
    return apiRequest("/api/auth/oauth/github/bind", {
      method: "POST",
      body: authData,
      token,
    });
  },
};

/**
 * Projects API
 */
export const projectsApi = {
  /**
   * Get all projects for current user
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Projects data
   */
  getAllProjects: (token) => {
    return apiRequest("/api/projects", {
      token,
    });
  },
  
  /**
   * Get project by ID
   * @param {string} id - Project ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Project data
   */
  getProject: (id, token) => {
    return apiRequest(`/api/projects/${id}`, {
      token,
    });
  },
  
  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Created project data
   */
  createProject: (projectData, token) => {
    return apiRequest("/api/projects", {
      method: "POST",
      body: projectData,
      token,
    });
  },
  
  /**
   * Update a project
   * @param {string} id - Project ID
   * @param {Object} projectData - Project data to update
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated project data
   */
  updateProject: (id, projectData, token) => {
    return apiRequest(`/api/projects/${id}`, {
      method: "PUT",
      body: projectData,
      token,
    });
  },
  
  /**
   * Delete a project
   * @param {string} id - Project ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Response data
   */
  deleteProject: (id, token) => {
    return apiRequest(`/api/projects/${id}`, {
      method: "DELETE",
      token,
    });
  },
};

/**
 * User API
 */
export const userApi = {
  /**
   * Get current user profile
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - User profile data
   */
  getProfile: (token) => {
    return apiRequest("/api/user/profile", {
      token,
    });
  },
  
  /**
   * Get user details including code platform bindings
   * @param {string} userId - User ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - User details data
   */
  getUserDetails: (userId, token) => {
    return apiRequest(`/api/user/${userId}/details`, {
      token,
    });
  },
  
  /**
   * Get user repositories
   * @param {string} userId - User ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - User repositories data
   */
  getUserRepositories: (userId, token) => {
    return apiRequest(`/api/user/${userId}/repositories`, {
      token,
    });
  },
  
  /**
   * Get repository by ID
   * @param {string} repositoryId - Repository ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Repository data
   */
  getRepositoryById: (repositoryId, token) => {
    return apiRequest(`/api/repositories/${repositoryId}`, {
      token,
    });
  },
  
  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated profile data
   */
  updateProfile: (profileData, token) => {
    return apiRequest("/api/user/profile", {
      method: "PUT",
      body: profileData,
      token,
    });
  },
};

// Create API service with all endpoints
const apiService = {
  auth: authApi,
  projects: projectsApi,
  user: userApi,
};

// Export default API service
export default apiService; 